import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import classNames from 'classnames';
import moment from 'moment';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
  ActionSelect,
  Button,
  BaseCard,
  BaseCardBody,
  BaseCardHeader,
  Icon,
  OverlaySpinner,
  Popover,
  useToast,
} from '@unite-us/app-components';
import { ScreeningDetailWrapper } from '@unite-us/app-screenings';
import findOrCreateClientRelationship from 'src/api/core/ClientRelationship/findOrCreateClientRelationship';
import getClientsDetailLink from 'src/common/utils/getClientsDetailLink';
import { browserHistory } from 'common/utils/browserHistory';
import * as apiHooks from 'src/api/APIHooks';
import { getAuthToken } from 'src/api/config';
import * as standardHooks from 'src/api/standardHooks';
import * as useEmployeeHooks from 'src/components/Organization/api/hooks/v1/employeeHooks';
import { env } from 'src/config/env';
import {
  hbh1025ShowEligibilityAssessmentTab,
  wrk253TaskViewEstablishClientRelationship,
} from 'src/common/utils/FeatureFlags/flags';
import { hasFeatureRole } from 'common/utils/Roles/hasFeatureRole';
import {
  useTaskById,
  useBatchUpdateTaskStatus,
  useClientRelationship,
  useEstablishClientRelationship,
} from './tasksQueries';
import BulkStatusUpdateModal from './BulkStatusUpdateModal';
import ReassignTasksModal from './ReassignTasksModal';
import TasksV3Sidebar from './TasksV3Sidebar';
import { TASK_STATUS_LABELS } from './constants';
import {
  notifyTaskStatusUpdatedSuccess,
  notifyTaskStatusUpdatedError,
  notifyClientRelationshipAcceptedSuccess,
  notifyClientRelationshipAcceptedError,
} from './taskNotifications';
import { getTaskPersonName } from './tasksUtils';
import './TasksV2.scss';

const CORE_HOOKS = {
  ...apiHooks,
  ...standardHooks,
  ...useEmployeeHooks,
};
const initialRiskDisplayState = {
  hasNeeds: false,
  requiresEligibilityAssessment: false,
  isSaving: false,
  onSearchResources: null,
  isReady: false,
};

const hasRenderedScreeningDetail = (container) => !!container.querySelector('.detail-view .screening-detail');

const observeScreeningDetailRender = (container, onRendered) => {
  const observer = new MutationObserver(() => {
    if (hasRenderedScreeningDetail(container)) {
      onRendered();
      observer.disconnect();
    }
  });

  observer.observe(container, { childList: true, subtree: true });

  return observer;
};

const TaskView = ({
  params,
  router,
  currentProviderId,
  currentEmployeeId,
  currentProvider,
  currentEmployee,
  taskId: taskIdProp,
  isTaskDrawer,
  hideNavigation,
  onOpenFullView,
  hasSocialCareNavigationRole,
  hbh1025ShowEligibilityAssessmentEnabled,
  wrk253TaskViewEstablishClientRelationshipEnabled,
}) => {
  const taskId = taskIdProp || params?.taskId;
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
  const [pendingStatusAction, setPendingStatusAction] = useState('complete');
  const [isScreeningWrapperVisible, setIsScreeningWrapperVisible] = useState(false);
  const [isPendingSearchResourcesOpen, setIsPendingSearchResourcesOpen] = useState(false);
  const [riskDisplayState, setRiskDisplayState] = useState(initialRiskDisplayState);
  const [hasAcceptedClientRelationship, setHasAcceptedClientRelationship] = useState(false);
  const { addToast } = useToast();
  const addToastRef = useRef(addToast);
  const screeningWrapperRef = useRef(null);
  const actionSelectRef = useRef(null);

  const { data: task, isLoading } = useTaskById(taskId);
  const { mutateAsync: batchUpdateTaskStatus, isLoading: isUpdatingStatus } = useBatchUpdateTaskStatus();

  const handleBack = () => { router.goBack(); };

  const handleAfterReassignSubmit = () => {
    setShowReassignModal(false);
  };
  const openStatusUpdateModal = (statusAction) => {
    setPendingStatusAction(statusAction);
    setShowStatusUpdateModal(true);
  };

  const handleStatusUpdateConfirm = async () => {
    if (!task?.id) return;

    try {
      await batchUpdateTaskStatus({
        taskIds: task.id,
        status: pendingStatusAction,
      });
      notifyTaskStatusUpdatedSuccess(pendingStatusAction, 1);
      setShowStatusUpdateModal(false);
      router.goBack();
    } catch (error) {
      notifyTaskStatusUpdatedError();
    }
  };

  const formatDate = (iso) => (iso ? moment(iso).format('M/D/YYYY h:mm A') : '—');
  const formatEnhancedPopulations = (initiativeConditions = []) => {
    const labels = initiativeConditions
      .map((condition) => condition?.initiative_description)
      .filter(Boolean);

    return labels.length ? Array.from(new Set(labels)).join(', ') : '—';
  };

  const person = task?.person ?? null;
  const personName = getTaskPersonName(task);
  const hasLinkedPersonName = Boolean(task?.person?.full_name);
  const personId = person?.id ?? task?.entity_id ?? null;
  const assigneeName = task?.assignee_employee?.full_name ?? null;
  const assignedProviderName = task?.assignee_provider?.name ?? null;
  const isArchivedTask = task?.status === 'archived';
  const isCompletable = task && ['to_do', 'overdue', 'archived'].includes(task.status);
  const isScreeningTask = task?.subject_type === 'Screening';
  const showElAssessmentTab = hasSocialCareNavigationRole && hbh1025ShowEligibilityAssessmentEnabled;

  const {
    data: clientRelationship,
    isLoading: isLoadingClientRelationship,
  } = useClientRelationship(personId, currentProviderId, {
    enabled: wrk253TaskViewEstablishClientRelationshipEnabled && !!personId && !!currentProviderId,
  });
  const {
    mutateAsync: establishClientRelationship,
    isLoading: isEstablishingClientRelationship,
  } = useEstablishClientRelationship();
  const hasClientRelationship = Array.isArray(clientRelationship) ?
    clientRelationship.length > 0 :
    Boolean(clientRelationship);
  const mustAcceptClientRelationship = Boolean(
    wrk253TaskViewEstablishClientRelationshipEnabled &&
    personId &&
    !isLoadingClientRelationship &&
    !hasClientRelationship &&
    !hasAcceptedClientRelationship,
  );

  useEffect(() => {
    setHasAcceptedClientRelationship(false);
  }, [personId, currentProviderId]);

  const handleAcceptClientRelationship = useCallback(async () => {
    try {
      await establishClientRelationship({ personId, providerId: currentProviderId });
      setHasAcceptedClientRelationship(true);
      notifyClientRelationshipAcceptedSuccess();
    } catch (error) {
      notifyClientRelationshipAcceptedError();
    }
  }, [establishClientRelationship, personId, currentProviderId]);

  const handleRejectClientRelationship = useCallback(() => {
    setShowReassignModal(true);
  }, []);

  useEffect(() => {
    addToastRef.current = addToast;
  }, [addToast]);

  useEffect(() => {
    setIsScreeningWrapperVisible(!isScreeningTask);
  }, [taskId, isScreeningTask]);

  const headerValueClass = isTaskDrawer ? 'py-2 px-1 capitalize min-h-9' : 'p-2 capitalize min-h-9';
  const headerLabelClass = classNames('text-text-blue font-semibold shrink-0 w-32', headerValueClass);

  const statusEl = (
    <p data-testid="task-detail-status" className={headerValueClass}>
      {TASK_STATUS_LABELS[task?.status] ?? task?.status ?? '—'}
    </p>
  );

  const clientNameContent = hasLinkedPersonName && personId ? (
    getClientsDetailLink({
      person,
      type: 'button',
      showArrow: true,
      fallbackName: personName,
      fallbackId: personId,
      onClick: () => browserHistory.push(`/facesheet/${personId}`),
      className: 'font-medium',
    })
  ) : personName;

  const clientEl = (
    <div data-testid="task-detail-client" className={headerValueClass}>
      {mustAcceptClientRelationship ? (
        <Popover
          id="task-detail-client-locked"
          content={(
            <span style={{ color: '#ffffff' }}>
              Client details cannot be accessed until you accept the client relationship.
            </span>
          )}
          dark
          showArrow
          trigger="hover"
          position="right"
          width="64"
        >
          <span className="inline-flex items-center gap-1">
            {personName}
            <Icon icon="Lock" color="action-blue" size={14} />
          </span>
        </Popover>
      ) : clientNameContent}
    </div>
  );

  const assignedToEl = (
    <p data-testid="task-detail-assigned-to" className={headerValueClass}>
      {assigneeName || '—'}
    </p>
  );
  const assignedOrgEl = (
    <p data-testid="task-detail-assigned-org" className={headerValueClass}>
      {assignedProviderName || '—'}
    </p>
  );
  const dueEl = (
    <p data-testid="task-detail-due" className={headerValueClass}>
      {formatDate(task?.due_at ?? task?.due_date)}
    </p>
  );
  const createdEl = (
    <p data-testid="task-detail-created" className={headerValueClass}>
      {formatDate(task?.created_at)}
    </p>
  );
  const updatedEl = (
    <p data-testid="task-detail-updated" className={headerValueClass}>
      {formatDate(task?.updated_at)}
    </p>
  );
  const enhancedPopulations = formatEnhancedPopulations(task?.person?.initiative_conditions);
  const identifiedNeedsCount = task?.task_reference?.screening_identified_needs_count ?? 0;
  let taskDescription = task?.related_info?.full_text || 'No details provided.';
  if (task?.description?.length > 0) {
    taskDescription = task.description;
  }
  if (task?.details?.length > 0) {
    taskDescription = task.details;
  }
  const {
    hasNeeds: screeningHasNeeds,
    isSaving,
    onSearchResources,
  } = riskDisplayState;
  const hasNeeds = screeningHasNeeds || Number(identifiedNeedsCount) > 0;
  const canShowScreeningActions = Number(identifiedNeedsCount) > 0;

  const ensureClientRelationship = useCallback(
    async () => findOrCreateClientRelationship(personId, currentProviderId),
    [currentProviderId, personId],
  );

  const openEligibilityAssessment = useCallback(async () => {
    if (isSaving) {
      return null;
    }

    await ensureClientRelationship();
    browserHistory.push(`/facesheet/${personId}/eligibility/new`);
    return null;
  }, [ensureClientRelationship, isSaving, personId]);

  const openSearchResources = useCallback(async () => {
    await ensureClientRelationship();

    if (onSearchResources) {
      return onSearchResources();
    }

    setIsPendingSearchResourcesOpen(true);
    return null;
  }, [ensureClientRelationship, onSearchResources]);

  const handleRiskDisplayUpdate = useCallback((next) => {
    setRiskDisplayState((prev) => ({ ...prev, ...next, isReady: true }));
  }, []);

  useLayoutEffect(() => {
    setRiskDisplayState(initialRiskDisplayState);
    setIsPendingSearchResourcesOpen(false);
  }, [task?.subject_id, taskId]);

  useEffect(() => {
    if (!isPendingSearchResourcesOpen || !onSearchResources) {
      return;
    }

    onSearchResources();
    setIsPendingSearchResourcesOpen(false);
  }, [isPendingSearchResourcesOpen, onSearchResources]);

  const screeningAppSettings = useMemo(() => ({
    env: {
      getAuthToken,
      coreUrl: env.CORE_API,
      employee: currentEmployee,
      providerId: currentProviderId,
      employeeId: currentEmployeeId,
      provider: currentProvider,
      screeningsApiUrl: `${env.SCREENING_BASE_URL}/v2`,
    },
  }), [
    currentEmployee,
    currentEmployeeId,
    currentProvider,
    currentProviderId,
  ]);

  const screeningCallbacks = useMemo(() => ({
    notify: {
      error: (message) => addToastRef.current({ message, type: 'error' }),
      success: (message) => addToastRef.current({ message, type: 'success' }),
      warn: (message) => addToastRef.current({ message, type: 'warning' }),
    },
    getClientsDetailLink,
    pushInHistory: async (url) => {
      const isResourceSearchPath = typeof url === 'string' && url.startsWith('/referrals/create/add-resources');

      if (isResourceSearchPath) {
        await ensureClientRelationship();
      }

      browserHistory.push(url);
    },
  }), [ensureClientRelationship]);

  useEffect(() => {
    if (!isScreeningTask || !task || !screeningWrapperRef.current) return undefined;

    const container = screeningWrapperRef.current;

    if (hasRenderedScreeningDetail(container)) {
      setIsScreeningWrapperVisible(true);
      return undefined;
    }

    const observer = observeScreeningDetailRender(
      container,
      () => setIsScreeningWrapperVisible(true),
    );

    return () => {
      observer.disconnect();
    };
  }, [isScreeningTask, taskId, task, mustAcceptClientRelationship]);

  return (
    <div className={`flex antialiased min-h-full ${hideNavigation ? '' : '-mx-container-padding'}`}>
      {!hideNavigation && <TasksV3Sidebar />}

      <div className="flex-1">
        <div
          className={`
            ${isTaskDrawer ? 'flex-col' : 'flex flex-wrap justify-between'}
            p-6 bg-white border-b border-solid border-dark-border-blue
          `}
        >
          <div className="flex gap-4">
            {!isTaskDrawer && (
              <Button
                secondary
                label="Back"
                icon={{
                  name: 'ChevronArrow',
                  color: 'text-blue',
                  position: 'left',
                  direction: 'left',
                }}
                className="font-regular-font z-0"
                onClick={handleBack}
              />
            )}
            {isTaskDrawer && onOpenFullView ? (
              <Button
                link
                label={isLoading ? 'Loading...' : (task?.name ?? '—')}
                className="my-auto font-semibold text-xl text-left p-0 hover:text-action-blue"
                onClick={() => onOpenFullView(task)}
              />
            ) : (
              <h1 className="my-auto font-semibold">
                {isLoading ? 'Loading...' : (task?.name ?? '—')}
              </h1>
            )}
          </div>
          <div className={`flex flex-wrap gap-4 ${isTaskDrawer ? 'mt-4' : ''}`}>
            {isCompletable && (
              <Button
                primary
                label="Mark as Complete"
                icon={{
                  name: 'CheckCircle',
                  color: 'white',
                  position: 'left',
                }}
                className="font-regular-font z-0"
                disabled={isUpdatingStatus}
                onClick={() => openStatusUpdateModal('complete')}
              />
            )}
            <Button
              secondary
              label="Assign"
              data-testid="assign-task-button"
              icon={{
                name: 'Edit',
                color: 'action-blue',
                position: 'left',
              }}
              disabled={isArchivedTask}
              className="font-regular-font z-0 text-action-blue"
              onClick={() => setShowReassignModal(true)}
            />
            <Button
              secondary
              label="Archive"
              icon={{
                name: 'Archive',
                color: 'action-blue',
                position: 'left',
              }}
              disabled={isArchivedTask || isUpdatingStatus}
              className="font-regular-font z-0 text-action-blue"
              onClick={() => openStatusUpdateModal('archived')}
            />
          </div>
        </div>

        <div className={isTaskDrawer ? 'bg-medium-fill-grey pt-2 pb-2 min-h-screen' : ''}>
          {isLoading && (
            <div className="w-11/12 mx-auto my-8">
              <BaseCard noBorder>
                <BaseCardBody className="relative min-h-[24rem]">
                  <OverlaySpinner show text="" />
                </BaseCardBody>
              </BaseCard>
            </div>
          )}

          {!isLoading && task && (
            <div className="w-11/12 mx-auto my-8">
              <BaseCard className="mb-6">
                <BaseCardHeader title="Task Details" className="text-xl font-semibold px-2" />
                <BaseCardBody>
                  {mustAcceptClientRelationship && (
                    <div className="bg-white px-6 pt-6">
                      <div
                        className={`
                          p-4 rounded border border-solid border-action-blue bg-light-fill-blue
                          flex flex-wrap items-start justify-between gap-4
                        `}
                      >
                        <div className="flex items-start gap-2">
                          <Icon
                            icon="InfoCircle"
                            color="action-blue"
                            className="flex-shrink-0 mt-px"
                          />
                          <div>
                            <p className="font-semibold">This task was assigned to you.</p>
                            <p className="text-sm">
                              In order to access client details and work on this task, you must accept the
                              client relationship.
                            </p>
                            <p className="text-sm italic">
                              Only accept if your organization intends to work with this client.
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <Button
                            secondary
                            label="Reject"
                            data-testid="reject-client-relationship-button"
                            disabled={isEstablishingClientRelationship}
                            onClick={handleRejectClientRelationship}
                          />
                          <Button
                            primary
                            label="Accept"
                            data-testid="accept-client-relationship-button"
                            disabled={isEstablishingClientRelationship}
                            onClick={handleAcceptClientRelationship}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {isTaskDrawer ? (
                    <div className="p-6 flex flex-col gap-y-1">
                      <div className="grid grid-cols-6 gap-4">
                        <p className={classNames(headerLabelClass, 'col-span-2 pl-0')}>Status:</p>
                        <div className="col-span-4">{statusEl}</div>
                      </div>
                      <div className="grid grid-cols-6 gap-4">
                        <p className={classNames(headerLabelClass, 'col-span-2 pl-0')}>Client:</p>
                        <div className="col-span-4">{clientEl}</div>
                      </div>
                      <div className="grid grid-cols-6 gap-4">
                        <p className={classNames(headerLabelClass, 'col-span-2 pl-0')}>Assigned To:</p>
                        <div className="col-span-4">{assignedToEl}</div>
                      </div>
                      <div className="grid grid-cols-6 gap-4">
                        <p className={classNames(headerLabelClass, 'col-span-2 pl-0')}>Assigned Org:</p>
                        <div className="col-span-4">{assignedOrgEl}</div>
                      </div>
                      <div className="grid grid-cols-6 gap-4">
                        <p className={classNames(headerLabelClass, 'col-span-2 pl-0')}>Created:</p>
                        <div className="col-span-4">{createdEl}</div>
                      </div>
                      <div className="grid grid-cols-6 gap-4">
                        <p className={classNames(headerLabelClass, 'col-span-2 pl-0')}>Last Updated:</p>
                        <div className="col-span-4">{updatedEl}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-6 grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-y-1">
                        <div className="flex">
                          <p className={headerLabelClass}>Status:</p>
                          <div>{statusEl}</div>
                        </div>
                        <div className="flex">
                          <p className={headerLabelClass}>Client:</p>
                          <div>{clientEl}</div>
                        </div>
                        <div className="flex">
                          <p className={headerLabelClass}>Assigned To:</p>
                          <div>{assignedToEl}</div>
                        </div>
                        <div className="flex">
                          <p className={headerLabelClass}>Assigned Org:</p>
                          <div>{assignedOrgEl}</div>
                        </div>
                      </div>
                      <div className="flex flex-col gap-y-1">
                        <div className="flex">
                          <p className={headerLabelClass}>Due:</p>
                          <div>{dueEl}</div>
                        </div>
                        <div className="flex">
                          <p className={headerLabelClass}>Created:</p>
                          <div>{createdEl}</div>
                        </div>
                        <div className="flex">
                          <p className={headerLabelClass}>Updated:</p>
                          <div>{updatedEl}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </BaseCardBody>
              </BaseCard>

              {isScreeningTask ? (
                <>
                  <BaseCard className="mb-6">
                    <BaseCardHeader title="Task" className="text-xl font-semibold px-2" />
                    <BaseCardBody withPadding>
                      {hasNeeds && (
                        <>
                          <p className="text-sm pb-2">
                            The screening has identified{' '}
                            <strong>{identifiedNeedsCount} client needs</strong>.
                            {' '}
                            Contact the client to complete an eligibility assessment for
                            enhanced services.
                          </p>
                          <p className="text-sm pb-4">
                            <strong>Enhanced Populations:</strong>
                            {' '}
                            {enhancedPopulations}
                          </p>
                        </>
                      )}
                      {canShowScreeningActions && (
                        mustAcceptClientRelationship ? (
                          <Popover
                            id="task-take-action-locked"
                            content={(
                              <span style={{ color: '#ffffff' }}>
                                You cannot take action on this task until you accept the client relationship.
                              </span>
                            )}
                            dark
                            showArrow
                            trigger="hover"
                            position="top-start"
                            width="64"
                            wrapperClassName="justify-start"
                          >
                            <ActionSelect
                              id="tasks-v3-screening-take-action"
                              disabled
                              actions={[]}
                              placeholder="Take Action"
                              styles={{ container: { width: '250px' } }}
                            />
                          </Popover>
                        ) : (
                          <ActionSelect
                            id="tasks-v3-screening-take-action"
                            ref={actionSelectRef}
                            actions={[
                              showElAssessmentTab && {
                                value: 'task-open-eligibility-assessment',
                                label: 'Open Eligibility Assessments',
                                icon: 'Assessment',
                                action: openEligibilityAssessment,
                              },
                              {
                                value: 'task-search-for-resources',
                                label: 'Search for Resources',
                                icon: 'Referral',
                                action: openSearchResources,
                              },
                            ].filter(Boolean)}
                            placeholder="Take Action"
                            styles={{ container: { width: '250px' } }}
                          />
                        )
                      )}
                      {!hasNeeds && (
                        <p className="pb-2">The screening has not identified any services for your client.</p>
                      )}
                    </BaseCardBody>
                  </BaseCard>

                  <BaseCard>
                    <BaseCardHeader title="Screening" className="text-xl font-semibold px-2" />
                    <BaseCardBody withPadding className="relative min-h-[24rem]">
                      {mustAcceptClientRelationship ? (
                        <p className="text-sm text-center text-gray-700 py-16" data-testid="screening-locked-message">
                          Unable to view screening information because a client relationship has not been
                          established.
                          {' '}
                          Accept the client relationship to work on the task.
                        </p>
                      ) : (
                        <>
                          {!isScreeningWrapperVisible && (
                            <div className="absolute inset-0 z-10 bg-white" />
                          )}
                          <div ref={screeningWrapperRef} className="rounded bg-white px-4 relative min-h-[24rem]">
                            <ScreeningDetailWrapper
                              source="workflows"
                              appSettings={screeningAppSettings}
                              hooks={CORE_HOOKS}
                              callbacks={{
                                ...screeningCallbacks,
                                onRiskDisplayUpdate: handleRiskDisplayUpdate,
                                onResourceSearchModalClose: () => actionSelectRef.current?.resetValue?.(),
                              }}
                              screeningId={task?.subject_id}
                              contactId={personId}
                              isCondensedView={isTaskDrawer}
                            />
                          </div>
                        </>
                      )}
                    </BaseCardBody>
                  </BaseCard>
                </>
              ) : (
                <BaseCard>
                  <BaseCardHeader title="Task" className="text-xl font-semibold px-2" />
                  <BaseCardBody withPadding>
                    <p className="text-sm text-gray-700">
                      {taskDescription}
                    </p>
                  </BaseCardBody>
                </BaseCard>
              )}
            </div>
          )}

          {!isLoading && !task && (
            <div className="flex items-center justify-center py-32 text-gray-400 text-sm">
              Task not found.
            </div>
          )}
        </div>
      </div>

      <ReassignTasksModal
        show={showReassignModal}
        onClose={() => setShowReassignModal(false)}
        onAfterSubmit={handleAfterReassignSubmit}
        selectedTasks={task ? [task] : []}
        currentProviderId={currentProviderId}
      />

      <BulkStatusUpdateModal
        show={showStatusUpdateModal}
        onClose={() => setShowStatusUpdateModal(false)}
        onConfirm={handleStatusUpdateConfirm}
        loading={isUpdatingStatus}
        statusAction={pendingStatusAction}
        selectedCount={1}
      />
    </div>
  );
};

TaskView.propTypes = {
  params: PropTypes.shape({
    taskId: PropTypes.string,
  }),
  router: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
    isActive: PropTypes.func.isRequired,
  }).isRequired,
  currentProviderId: PropTypes.string,
  currentEmployeeId: PropTypes.string,
  currentProvider: PropTypes.object,
  currentEmployee: PropTypes.object,
  taskId: PropTypes.string,
  isTaskDrawer: PropTypes.bool,
  hideNavigation: PropTypes.bool,
  onOpenFullView: PropTypes.func,
  hasSocialCareNavigationRole: PropTypes.bool,
  hbh1025ShowEligibilityAssessmentEnabled: PropTypes.bool,
  wrk253TaskViewEstablishClientRelationshipEnabled: PropTypes.bool,
};

TaskView.defaultProps = {
  params: {},
  currentProviderId: null,
  currentEmployeeId: null,
  currentProvider: null,
  currentEmployee: null,
  taskId: null,
  isTaskDrawer: false,
  hideNavigation: false,
  onOpenFullView: null,
  hasSocialCareNavigationRole: false,
  hbh1025ShowEligibilityAssessmentEnabled: false,
  wrk253TaskViewEstablishClientRelationshipEnabled: false,
};

function mapStateToProps(state) {
  return {
    currentProviderId: get(state, 'globalState.currentProvider.group.id', null),
    currentEmployeeId: get(state, 'globalState.currentEmployee.id', null),
    currentProvider: get(state, 'globalState.currentProvider.group', null),
    currentEmployee: get(state, 'globalState.currentEmployee', null),
    hasSocialCareNavigationRole: hasFeatureRole({
      employeeRoles: get(state, 'globalState.currentEmployee.roles', []),
      targetRoleKey: 'social_care_navigation',
    }),
    hbh1025ShowEligibilityAssessmentEnabled: hbh1025ShowEligibilityAssessmentTab(state),
    wrk253TaskViewEstablishClientRelationshipEnabled: wrk253TaskViewEstablishClientRelationship(state),
  };
}

export default connect(mapStateToProps)(withRouter(TaskView));
